/**
 * Use a weetabix format index
 */

import CSV from 'comma-separated-values';

class BaseReader {
    constructor(source_url, { index = null } = {}) {
        this._source = source_url;
        this._index_src = index || `${source_url}.wtb`;

        // Information to read from the index (once ready)
        this._delimiter = null;
        this._index = null;
        this._ready = false;
    }

    /**
     * Load the index file from the specified source. Returns a promise that will resolve when the reader is ready to
     *  use.
     * @returns {Promise<Reader | never>}
     */
    prepare() {
        // Load data from the index, and return a promise
        let loader;
        if (typeof this._index_src === 'string') {
            // TODO: Make mode configurable
            loader = fetch(this._index_src, {mode: 'cors'}).then(resp => {
                if (resp.ok) {
                    return resp.json();
                } else {
                    throw new Error(`Index could not be read from ${this._index_src}`);
                }
            });
        } else {
            // Allows specifying the index inline with the page, rather than as a separate download
            loader = Promise.resolve(this._index_src);
        }

        return loader.then(contents => {
            this._delimiter = contents.delimiter;
            this._index = contents.index;
            this._ready = true;
            return this;
        });
    }

    /**
     * Fetch the section of the file containing the data of interest
     * @param value The category of name to retrieve from the index
     * @param options Arguments to be passed to the network request. See JS "fetch" documentation for details.
     * @param strict
     * @returns {Promise<Array[]>}
     */
    fetch(value, { options = {}, strict = true } = {}) {
        const entry = this._findValue([value]);
        if (!entry && !strict) {
            return Promise.resolve([]);
        } else if (!entry) {
            throw new Error('The index has no information about the requested item');
        }
        const [start, end] = entry;

        options.mode = 'cors';  // TODO: Make configurable
        options.headers = Object.assign({
            'Range': `bytes=${start}-${end}`,
        }, (options.headers || {}));

        return fetch(this._source, options).then(resp => {
            if (!resp.ok || resp.status !== 206) {
                // Safeguard: do not parse unless the server indicates it is responding to a partial request.
                //   Some web hosts (and many local development tools) do not properly support range headers!
                throw new Error('Could not successfully request partial data');
            }
            return resp.text();
        }).then(data => CSV.parse(data, {cellDelimiter: this._delimiter, cast: false}));
    }

    /////// Most reader subclasses will involve a different index format, and will only need to override the methods
    //  below this line.
    /**
     * Search the index for a desired value. Default implementation looks for exact match as key name.
     * @param value
     * @returns [] The start and end coordinates (or null if item not in index)
     * @private
     */
    _findValue(value) {
        return this._index[value];
    }

    getEntries() {
        if (!this._ready) {
            throw new Error('Index data has not yet been loaded.');
        }
        return Object.keys(this._index);
    }
}


// For now a category reader is the only kind; this may change.
class CategoryReader extends BaseReader {}

export default CategoryReader;
export { CategoryReader, BaseReader };
